import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCredits } from '../../context/CreditContext';
import Icons from '../Icons';

export default function TopBar({ onShowAuth, isSidebarCollapsed, setIsSidebarCollapsed, isMobileSidebarOpen, setIsMobileSidebarOpen }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { credits, plan, profile } = useCredits();
  const [showHelpMenu, setShowHelpMenu] = React.useState(false);

  const isSubscribed = plan && plan !== 'free';

  // Check if subscription is expiring within 3 days
  const expiryWarning = useMemo(() => {
    if (!profile?.planExpiry) return null;
    const expiry = new Date(profile.planExpiry);
    const now = new Date();
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 0) return { text: 'Your subscription has expired!', urgent: true };
    if (daysLeft <= 3) return { text: `Subscription expires in ${daysLeft} day${daysLeft > 1 ? 's' : ''}`, urgent: daysLeft <= 1 };
    return null;
  }, [profile?.planExpiry]);

  const showBuyCredits = credits < 30 || credits === 0;

  return (
    <header className="top">
      {/* Mobile Hamburger */}
      <button className="top-hamburger mobile-only" onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}>
        <Icons.Menu sz={20} />
      </button>

      {/* PC Hamburger / Collapse toggle */}
      <button className="top-hamburger pc-only" onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
        <Icons.Menu sz={20} />
      </button>

      <div className="logo" onClick={() => navigate('/home')}>
        <img src="/logo.png" alt="aiField" style={{ height: 28, width: 'auto' }} />
      </div>
      <div className="top-sep" />

      {/* If NOT logged in — show Sign up button */}
      {!user && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="tbtn" onClick={() => navigate('/subscription')} style={{ fontWeight: 700 }}>
            Pricing
          </button>
          <button className="tbtn signup-btn" onClick={onShowAuth}>
            ✨ Sign up for free
          </button>
        </div>
      )}

      {/* Logged in content */}
      {user && (
        <>
          {/* Subscription expiry warning */}
          {expiryWarning && (
            <div onClick={() => navigate('/subscription')} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 14px', borderRadius: 99, cursor: 'pointer',
              background: expiryWarning.urgent ? 'rgba(239,68,68,.1)' : 'rgba(245,158,11,.1)',
              border: `1px solid ${expiryWarning.urgent ? 'rgba(239,68,68,.2)' : 'rgba(245,158,11,.2)'}`,
              color: expiryWarning.urgent ? '#f87171' : '#fbbf24',
              fontSize: 11, fontWeight: 700,
              animation: expiryWarning.urgent ? 'gpulse 2s ease-in-out infinite' : 'none',
            }}>
              ⚠️ {expiryWarning.text}
            </div>
          )}

          {/* Credit pill */}
          <div className="cr-pill" onClick={() => navigate('/subscription')} style={
            credits < 30 ? { background: 'rgba(239,68,68,.08)', borderColor: 'rgba(239,68,68,.15)', color: '#f87171' } : {}
          }>
            <Icons.Coin /> {credits} credits
            {credits < 30 && <span style={{ fontSize: 9, opacity: .7 }}> (low)</span>}
          </div>

          {/* Upgrade button */}
          {!isSubscribed && (
            <button className="tbtn up" onClick={() => navigate('/subscription')}>
              <Icons.Crown sz={13} /> Upgrade
            </button>
          )}

          {/* Buy Credits */}
          {(isSubscribed && showBuyCredits) && (
            <button className="tbtn" onClick={() => navigate('/subscription')}>
              Buy Credits
            </button>
          )}
        </>
      )}

      {/* Help Menu */}
      <div style={{ position: 'relative' }}>
        <button className="tbtn" style={{ padding: '0 8px' }} onClick={() => setShowHelpMenu(!showHelpMenu)}>
          <Icons.Help sz={18} />
        </button>
        {showHelpMenu && (
          <>
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 90 }} onClick={() => setShowHelpMenu(false)} />
            <div style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 8,
              background: 'var(--bg2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12,
              padding: 6, minWidth: 180, zIndex: 100,
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
              display: 'flex', flexDirection: 'column', gap: 2
            }}>
              {[
                { path: '/whats-new', label: "What's New", icon: '✨' },
                { path: '/guide', label: 'User Guide', icon: '📖' },
                { path: '/help', label: 'Help Center', icon: '📚' },
                { path: '/feedback', label: 'Send Feedback', icon: '💬' },
                { path: '/privacy', label: 'Privacy & Terms', icon: '🛡️' }
              ].map(item => (
                <div key={item.path} onClick={() => { navigate(item.path); setShowHelpMenu(false); }}
                  style={{
                    padding: '8px 12px', fontSize: 13, color: 'var(--t2)', borderRadius: 8,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600,
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ fontSize: 14 }}>{item.icon}</span> {item.label}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </header>
  );
}
